import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
//Import Components
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
import CustomTabs from 'components/common/CustomTabs';
import LabelTag from 'components/common/LabelTag';
import FireBaseSettingsContent from './FireBaseSettingsContent';
import OneSignalSettingsContent from './OneSignalSettingsContent';
import SectionWithTitle from 'components/common/SectionWithTitle';
//Import Constants
import { ActionsIcons, AlertTypes, l, ModalsClassNames, PushNotificationProviderKeys } from 'constants/common';
//Import Service
import { ProviderHttpService } from 'services/http';
//Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useToaster from 'hooks/UseToaster';
import { PushNotificationProvider } from 'types';

const { success } = AlertTypes;

const PushNotificationSettingsSection = () => {
    const { t } = useTranslation();
    const { doGetRequest, doPostRequest } = useRequest();
    const { showToaster } = useToaster();

    const [isPushNotificationProvidersLoading, setIsPushNotificationProvidersLoading] = useState(false);
    const [pushNotificationProviders, setPushNotificationProviders] =
        useState<Nullable<PushNotificationProvider>>(null);

    const [isSetDefaultModalOpen, setIsSetDefaultModalOpen] = useState(false);
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [modalData, setModalData] = useState<Nullable<AnyObject>>(null);

    const { getPushNotificationProviders, setDefaultPushNotificationProvider } = useCancelableRequests({
        getPushNotificationProviders: ProviderHttpService.getPushNotificationProviders,
        setDefaultPushNotificationProvider: ProviderHttpService.setDefaultPushNotificationProvider,
    });

    const defaultLabel = useMemo(
        () => <LabelTag color={`var(${`--status-label-goal-color`})`} labelName={t(l.Default)} />,
        [t],
    );

    const getPushNotificationProvidersData = useCallback(() => {
        setIsPushNotificationProvidersLoading(true);

        doGetRequest(getPushNotificationProviders.request, {
            successCallback: (data) => {
                if (isNil(data)) return;

                setPushNotificationProviders(data);
            },
        }).finally(() => {
            setIsPushNotificationProvidersLoading(false);
        });
    }, [doGetRequest, getPushNotificationProviders.request]);

    const setDefaultPushNotificationProviderAction = useCallback(
        (notificationType) => {
            const firebaseKey = PushNotificationProviderKeys.FireBase;
            setModalData({
                notificationType,
                DefaultProviderName: notificationType === firebaseKey ? t(l.OneSignal) : t(l.FireBase),
                Name: notificationType === firebaseKey ? t(l.FireBase) : t(l.OneSignal),
            });
            setIsSetDefaultModalOpen(true);
        },
        [t],
    );

    const onSetDefaultHandler = () => {
        setIsModalLoading(true);

        doPostRequest(setDefaultPushNotificationProvider.request, {
            queryString: {
                notificationType: modalData?.notificationType,
            },
            successCallback: () => {
                getPushNotificationProvidersData();

                showToaster(success, t(l.PushNotificationProviderSuccessfullySetAsDefault));
                onCloseModalHandler();
            },
        }).finally(() => {
            setIsModalLoading(false);
        });
    };

    const onCloseModalHandler = () => {
        setIsSetDefaultModalOpen(false);
        setIsModalLoading(false);
        setModalData(null);
    };

    const init = () => {
        getPushNotificationProvidersData();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    const tabsConfig = useMemo(() => {
        const baseProps = {
            isLoading: isPushNotificationProvidersLoading,
            onRefetchData: getPushNotificationProvidersData,
            onSetDefault: setDefaultPushNotificationProviderAction,
        };

        return [
            {
                tabTitle: (
                    <div className="crm-push-notification-settings-tab-title">
                        {t(l.FireBase)}
                        {pushNotificationProviders?.DefaultNotificationType === PushNotificationProviderKeys.FireBase &&
                            defaultLabel}
                    </div>
                ),
                tabKey: '0',
                tabContent: (
                    <FireBaseSettingsContent
                        {...baseProps}
                        initialData={pushNotificationProviders?.Firebase}
                        isDefault={
                            pushNotificationProviders?.DefaultNotificationType === PushNotificationProviderKeys.FireBase
                        }
                    />
                ),
            },
            {
                tabTitle: (
                    <div className="crm-push-notification-settings-tab-title">
                        {t(l.OneSignal)}
                        {pushNotificationProviders?.DefaultNotificationType ===
                            PushNotificationProviderKeys.OneSignal && defaultLabel}
                    </div>
                ),
                tabKey: '1',
                tabContent: (
                    <OneSignalSettingsContent
                        {...baseProps}
                        initialData={pushNotificationProviders?.OneSignal}
                        isDefault={
                            pushNotificationProviders?.DefaultNotificationType ===
                            PushNotificationProviderKeys.OneSignal
                        }
                    />
                ),
            },
        ];
    }, [
        defaultLabel,
        getPushNotificationProvidersData,
        isPushNotificationProvidersLoading,
        pushNotificationProviders,
        setDefaultPushNotificationProviderAction,
        t,
    ]);

    return (
        <>
            <SectionWithTitle
                title={t(l.PushNotificationSettings)}
                hasContentPadding={false}
                className="crm-push-notification-provider-settings-wrapper"
                contentClassName="crm-push-notification-settings-content"
            >
                <CustomTabs tabsConfig={tabsConfig} />
            </SectionWithTitle>
            {isSetDefaultModalOpen && (
                <ConfirmationModal
                    onOk={onSetDefaultHandler}
                    onCancel={onCloseModalHandler}
                    isVisibleModal
                    titleText={t(l.SetDefaultPushNotificationProvider)}
                    questionLabel={t(l.AreYouSureYouWantToSetDefaultProvider, {
                        defaultProviderName: `"${modalData?.DefaultProviderName}"`,
                        currentProviderName: `"${modalData?.Name}"`,
                    })}
                    isLoading={isModalLoading}
                    iconType={ActionsIcons.SetDefault}
                    actionLabel={t(l.SetDefault)}
                    className={ModalsClassNames.Start}
                />
            )}
        </>
    );
};

export default memo(PushNotificationSettingsSection);
